import React, { useState } from "react";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 640);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

function InputField({ label, value, onChange, type = "number", prefix, suffix, placeholder, min = "0" }) {
  return (
    <div style={s.fieldGroup}>
      <label style={s.label}>{label}</label>
      <div style={s.inputWrap}>
        {prefix && <span style={s.affix}>{prefix}</span>}
        <input
          style={{ ...s.input, ...(prefix ? s.inputWithPrefix : {}), ...(suffix ? s.inputWithSuffix : {}) }}
          type={type} value={value} min={min} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span style={{ ...s.affix, ...s.suffixAffix }}>{suffix}</span>}
      </div>
    </div>
  );
}

function ResultCard({ label, value, color = "#4F7FFA", small = false }) {
  return (
    <div style={s.resultCard}>
      <div style={s.resultLabel}>{label}</div>
      <div style={{ ...s.resultValue, color, fontSize: small ? 18 : 24 }}>{value}</div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
function CalcCard({ title, icon, color, children, isActive, onClick }) {
  return (
    <div style={{ ...s.calcCard, ...(isActive ? { borderColor: color, boxShadow: `0 0 0 3px ${color}22` } : {}) }}>
      <div style={{ ...s.calcCardHeader, background: color + "15", cursor: "pointer" }} onClick={onClick}>
        <span style={s.calcIcon}>{icon}</span>
        <span style={{ ...s.calcTitle, color }}>{title}</span>
        <span style={s.calcChevron}>{isActive ? "▲" : "▼"}</span>
      </div>
      {isActive && <div style={s.calcBody}>{children}</div>}
    </div>
  );
}

// 1. Debt Payoff Calculator
function DebtPayoff() {
  const [balance, setBalance] = useState("5000");
  const [rate, setRate] = useState("19.99");
  const [payment, setPayment] = useState("150");

  const b = parseFloat(balance) || 0;
  const r = parseFloat(rate) / 100 / 12;
  const p = parseFloat(payment) || 0;
  const minPayment = b * r;

  let months = null, totalInterest = null;
  if (p > minPayment && b > 0) {
    months = Math.ceil(Math.log(p / (p - b * r)) / Math.log(1 + r));
    totalInterest = months * p - b;
  }

  const payoffDate = months ? (() => {
    const d = new Date(); d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  })() : null;

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Current Balance ($)" value={balance} onChange={setBalance} prefix="$" />
        <InputField label="Annual Interest Rate (%)" value={rate} onChange={setRate} suffix="%" />
        <InputField label="Monthly Payment ($)" value={payment} onChange={setPayment} prefix="$" />
      </div>
      {p <= minPayment && b > 0 && (
        <div style={s.warningBox}>⚠️ Payment too low — minimum needed is {formatCurrency(minPayment + 0.01)} to make progress.</div>
      )}
      {months && (
        <div style={s.resultsGrid}>
          <ResultCard label="Months to payoff" value={`${months} months`} color="#4F7FFA" />
          <ResultCard label="Payoff date" value={payoffDate} color="#10B981" />
          <ResultCard label="Total interest paid" value={formatCurrency(totalInterest)} color="#EF4444" />
          <ResultCard label="Total amount paid" value={formatCurrency(b + totalInterest)} color="#F97316" />
        </div>
      )}
    </div>
  );
}

// 2. Loan Payment Calculator
function LoanPayment() {
  const [amount, setAmount] = useState("25000");
  const [rate, setRate] = useState("7.5");
  const [years, setYears] = useState("5");

  const p = parseFloat(amount) || 0;
  const r = parseFloat(rate) / 100 / 12;
  const n = (parseFloat(years) || 0) * 12;

  let monthly = 0, totalPaid = 0, totalInterest = 0;
  if (p > 0 && r > 0 && n > 0) {
    monthly = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    totalPaid = monthly * n;
    totalInterest = totalPaid - p;
  }

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Loan Amount ($)" value={amount} onChange={setAmount} prefix="$" />
        <InputField label="Annual Interest Rate (%)" value={rate} onChange={setRate} suffix="%" />
        <InputField label="Loan Term (years)" value={years} onChange={setYears} suffix="yrs" />
      </div>
      {monthly > 0 && (
        <div style={s.resultsGrid}>
          <ResultCard label="Monthly payment" value={formatCurrency(monthly)} color="#4F7FFA" />
          <ResultCard label="Total amount paid" value={formatCurrency(totalPaid)} color="#F97316" />
          <ResultCard label="Total interest paid" value={formatCurrency(totalInterest)} color="#EF4444" />
          <ResultCard label="Interest as % of loan" value={`${((totalInterest / p) * 100).toFixed(1)}%`} color="#A855F7" />
        </div>
      )}
    </div>
  );
}

// 3. Credit Card Payoff Calculator
function CreditCardPayoff() {
  const [balance, setBalance] = useState("3500");
  const [rate, setRate] = useState("22.99");
  const [mode, setMode] = useState("payment"); // payment or date
  const [payment, setPayment] = useState("100");
  const [targetMonths, setTargetMonths] = useState("24");

  const b = parseFloat(balance) || 0;
  const r = parseFloat(rate) / 100 / 12;
  const p = parseFloat(payment) || 0;
  const n = parseFloat(targetMonths) || 0;

  let result = {};
  if (mode === "payment" && p > b * r && b > 0) {
    const months = Math.ceil(Math.log(p / (p - b * r)) / Math.log(1 + r));
    result = { months, totalInterest: months * p - b, totalPaid: months * p };
  } else if (mode === "date" && n > 0 && b > 0) {
    const required = r === 0 ? b / n : (b * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    result = { requiredPayment: required, totalInterest: required * n - b, totalPaid: required * n };
  }

  return (
    <div>
      <div style={s.toggleRow}>
        <button style={{ ...s.toggleBtn, ...(mode === "payment" ? s.toggleBtnActive : {}) }} onClick={() => setMode("payment")}>
          I know my payment
        </button>
        <button style={{ ...s.toggleBtn, ...(mode === "date" ? s.toggleBtnActive : {}) }} onClick={() => setMode("date")}>
          I have a target date
        </button>
      </div>
      <div style={s.inputGrid}>
        <InputField label="Card Balance ($)" value={balance} onChange={setBalance} prefix="$" />
        <InputField label="Annual Interest Rate (%)" value={rate} onChange={setRate} suffix="%" />
        {mode === "payment"
          ? <InputField label="Monthly Payment ($)" value={payment} onChange={setPayment} prefix="$" />
          : <InputField label="Months to Pay Off" value={targetMonths} onChange={setTargetMonths} suffix="mo" />}
      </div>
      {Object.keys(result).length > 0 && (
        <div style={s.resultsGrid}>
          {mode === "payment" && <ResultCard label="Months to payoff" value={`${result.months} months`} color="#4F7FFA" />}
          {mode === "date" && <ResultCard label="Required monthly payment" value={formatCurrency(result.requiredPayment)} color="#4F7FFA" />}
          <ResultCard label="Total interest paid" value={formatCurrency(result.totalInterest)} color="#EF4444" />
          <ResultCard label="Total amount paid" value={formatCurrency(result.totalPaid)} color="#F97316" />
        </div>
      )}
    </div>
  );
}

// 4. Compound Interest Calculator
function CompoundInterest() {
  const [principal, setPrincipal] = useState("10000");
  const [rate, setRate] = useState("7");
  const [years, setYears] = useState("10");
  const [monthly, setMonthly] = useState("100");
  const [frequency] = useState("12"); // eslint-disable-line

  const p = parseFloat(principal) || 0;
  const r = parseFloat(rate) / 100;
  const n = parseFloat(frequency) || 12;
  const t = parseFloat(years) || 0;
  const m = parseFloat(monthly) || 0;

  const futureValue = p * Math.pow(1 + r / n, n * t) +
    m * ((Math.pow(1 + r / n, n * t) - 1) / (r / n));
  const totalContributions = p + m * 12 * t;
  const interestEarned = futureValue - totalContributions;

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Initial Amount ($)" value={principal} onChange={setPrincipal} prefix="$" />
        <InputField label="Annual Interest Rate (%)" value={rate} onChange={setRate} suffix="%" />
        <InputField label="Time Period (years)" value={years} onChange={setYears} suffix="yrs" />
        <InputField label="Monthly Contribution ($)" value={monthly} onChange={setMonthly} prefix="$" />
      </div>
      <div style={s.resultsGrid}>
        <ResultCard label="Future value" value={formatCurrency(futureValue)} color="#10B981" />
        <ResultCard label="Interest earned" value={formatCurrency(interestEarned)} color="#4F7FFA" />
        <ResultCard label="Total contributions" value={formatCurrency(totalContributions)} color="#F97316" />
        <ResultCard label="Return on investment" value={`${((interestEarned / totalContributions) * 100).toFixed(1)}%`} color="#A855F7" />
      </div>
    </div>
  );
}

// 5. Budget Calculator
function BudgetCalc() {
  const [income, setIncome] = useState("5000");
  const [housing, setHousing] = useState("1500");
  const [transport, setTransport] = useState("400");
  const [food, setFood] = useState("500");
  const [utilities, setUtilities] = useState("200");
  const [insurance, setInsurance] = useState("300");
  const [debt, setDebt] = useState("400");
  const [entertainment, setEntertainment] = useState("200");
  const [other, setOther] = useState("100");

  const inc = parseFloat(income) || 0;
  const expenses = [
    { label: "Housing", val: parseFloat(housing) || 0, color: "#4F7FFA" },
    { label: "Transport", val: parseFloat(transport) || 0, color: "#F97316" },
    { label: "Food", val: parseFloat(food) || 0, color: "#10B981" },
    { label: "Utilities", val: parseFloat(utilities) || 0, color: "#A855F7" },
    { label: "Insurance", val: parseFloat(insurance) || 0, color: "#F59E0B" },
    { label: "Debt payments", val: parseFloat(debt) || 0, color: "#EF4444" },
    { label: "Entertainment", val: parseFloat(entertainment) || 0, color: "#EC4899" },
    { label: "Other", val: parseFloat(other) || 0, color: "#6B7280" },
  ];
  const totalExpenses = expenses.reduce((s, e) => s + e.val, 0);
  const remaining = inc - totalExpenses;

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Monthly Income ($)" value={income} onChange={setIncome} prefix="$" />
        <InputField label="Housing / Rent ($)" value={housing} onChange={setHousing} prefix="$" />
        <InputField label="Transportation ($)" value={transport} onChange={setTransport} prefix="$" />
        <InputField label="Food / Groceries ($)" value={food} onChange={setFood} prefix="$" />
        <InputField label="Utilities ($)" value={utilities} onChange={setUtilities} prefix="$" />
        <InputField label="Insurance ($)" value={insurance} onChange={setInsurance} prefix="$" />
        <InputField label="Debt Payments ($)" value={debt} onChange={setDebt} prefix="$" />
        <InputField label="Entertainment ($)" value={entertainment} onChange={setEntertainment} prefix="$" />
        <InputField label="Other ($)" value={other} onChange={setOther} prefix="$" />
      </div>
      <div style={s.resultsGrid}>
        <ResultCard label="Monthly income" value={formatCurrency(inc)} color="#10B981" />
        <ResultCard label="Total expenses" value={formatCurrency(totalExpenses)} color="#EF4444" />
        <ResultCard label="Remaining" value={formatCurrency(remaining)} color={remaining >= 0 ? "#10B981" : "#EF4444"} />
        <ResultCard label="Expense ratio" value={`${inc > 0 ? ((totalExpenses / inc) * 100).toFixed(1) : 0}%`} color="#4F7FFA" />
      </div>
      <div style={s.budgetBars}>
        {expenses.map((e) => (
          <div key={e.label} style={s.budgetBarRow}>
            <div style={s.budgetBarLabel}>{e.label}</div>
            <div style={s.budgetBarBg}>
              <div style={{ ...s.budgetBarFill, width: `${inc > 0 ? Math.min((e.val / inc) * 100, 100) : 0}%`, background: e.color }} />
            </div>
            <div style={s.budgetBarAmt}>{formatCurrency(e.val)}</div>
            <div style={s.budgetBarPct}>{inc > 0 ? `${((e.val / inc) * 100).toFixed(1)}%` : "0%"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 6. Debt-to-Income Ratio
function DebtToIncome() {
  const [grossIncome, setGrossIncome] = useState("6000");
  const [mortgage, setMortgage] = useState("1200");
  const [carLoan, setCarLoan] = useState("350");
  const [studentLoan, setStudentLoan] = useState("250");
  const [creditCards, setCreditCards] = useState("150");
  const [otherDebt, setOtherDebt] = useState("0");

  const income = parseFloat(grossIncome) || 0;
  const totalDebt = (parseFloat(mortgage) || 0) + (parseFloat(carLoan) || 0) +
    (parseFloat(studentLoan) || 0) + (parseFloat(creditCards) || 0) + (parseFloat(otherDebt) || 0);
  const dti = income > 0 ? (totalDebt / income) * 100 : 0;

  const getDtiStatus = () => {
    if (dti <= 20) return { label: "Excellent", color: "#10B981", desc: "Lenders love this. You have strong borrowing power." };
    if (dti <= 35) return { label: "Good", color: "#4F7FFA", desc: "Healthy ratio. Most lenders will approve you." };
    if (dti <= 43) return { label: "Acceptable", color: "#F59E0B", desc: "Borderline. Some lenders may hesitate." };
    if (dti <= 50) return { label: "High", color: "#F97316", desc: "Risky. Focus on reducing debt before taking on more." };
    return { label: "Very High", color: "#EF4444", desc: "Danger zone. Prioritize debt payoff immediately." };
  };

  const status = getDtiStatus();

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Gross Monthly Income ($)" value={grossIncome} onChange={setGrossIncome} prefix="$" />
        <InputField label="Mortgage / Rent ($)" value={mortgage} onChange={setMortgage} prefix="$" />
        <InputField label="Car Loan ($)" value={carLoan} onChange={setCarLoan} prefix="$" />
        <InputField label="Student Loans ($)" value={studentLoan} onChange={setStudentLoan} prefix="$" />
        <InputField label="Credit Card Minimums ($)" value={creditCards} onChange={setCreditCards} prefix="$" />
        <InputField label="Other Debt ($)" value={otherDebt} onChange={setOtherDebt} prefix="$" />
      </div>
      <div style={s.resultsGrid}>
        <ResultCard label="Total monthly debt" value={formatCurrency(totalDebt)} color="#EF4444" />
        <ResultCard label="Gross monthly income" value={formatCurrency(income)} color="#10B981" />
        <ResultCard label="DTI Ratio" value={`${dti.toFixed(1)}%`} color={status.color} />
        <ResultCard label="Status" value={status.label} color={status.color} />
      </div>
      <div style={{ ...s.warningBox, background: status.color + "15", borderColor: status.color + "44", color: "#333" }}>
        <strong style={{ color: status.color }}>{status.label}:</strong> {status.desc}
      </div>
      <div style={s.dtiBar}>
        <div style={{ ...s.dtiBarFill, width: `${Math.min(dti, 100)}%`, background: status.color }} />
        {[20, 35, 43, 50].map((mark) => (
          <div key={mark} style={{ ...s.dtiMark, left: `${mark}%` }}>
            <div style={s.dtiMarkLine} />
            <div style={s.dtiMarkLabel}>{mark}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// 7. Net Worth Calculator
function NetWorth() {
  const [checking, setChecking] = useState("5000");
  const [savings, setSavings] = useState("15000");
  const [investments, setInvestments] = useState("30000");
  const [home, setHome] = useState("0");
  const [car, setCar] = useState("8000");
  const [otherAssets, setOtherAssets] = useState("0");
  const [mortgage, setMortgage] = useState("0");
  const [carLoan, setCarLoan] = useState("5000");
  const [studentLoans, setStudentLoans] = useState("20000");
  const [creditCardDebt, setCreditCardDebt] = useState("3000");
  const [otherLiabilities, setOtherLiabilities] = useState("0");

  const assets = (parseFloat(checking) || 0) + (parseFloat(savings) || 0) + (parseFloat(investments) || 0) +
    (parseFloat(home) || 0) + (parseFloat(car) || 0) + (parseFloat(otherAssets) || 0);
  const liabilities = (parseFloat(mortgage) || 0) + (parseFloat(carLoan) || 0) +
    (parseFloat(studentLoans) || 0) + (parseFloat(creditCardDebt) || 0) + (parseFloat(otherLiabilities) || 0);
  const netWorth = assets - liabilities;

  return (
    <div>
      <div style={s.twoColSection}>
        <div>
          <div style={s.sectionSubtitle}>💚 Assets</div>
          <div style={s.inputStack}>
            <InputField label="Checking Account ($)" value={checking} onChange={setChecking} prefix="$" />
            <InputField label="Savings Account ($)" value={savings} onChange={setSavings} prefix="$" />
            <InputField label="Investments / 401k ($)" value={investments} onChange={setInvestments} prefix="$" />
            <InputField label="Home Value ($)" value={home} onChange={setHome} prefix="$" />
            <InputField label="Vehicle Value ($)" value={car} onChange={setCar} prefix="$" />
            <InputField label="Other Assets ($)" value={otherAssets} onChange={setOtherAssets} prefix="$" />
          </div>
        </div>
        <div>
          <div style={s.sectionSubtitle}>🔴 Liabilities</div>
          <div style={s.inputStack}>
            <InputField label="Mortgage Balance ($)" value={mortgage} onChange={setMortgage} prefix="$" />
            <InputField label="Car Loan Balance ($)" value={carLoan} onChange={setCarLoan} prefix="$" />
            <InputField label="Student Loans ($)" value={studentLoans} onChange={setStudentLoans} prefix="$" />
            <InputField label="Credit Card Debt ($)" value={creditCardDebt} onChange={setCreditCardDebt} prefix="$" />
            <InputField label="Other Liabilities ($)" value={otherLiabilities} onChange={setOtherLiabilities} prefix="$" />
          </div>
        </div>
      </div>
      <div style={s.resultsGrid}>
        <ResultCard label="Total assets" value={formatCurrency(assets)} color="#10B981" />
        <ResultCard label="Total liabilities" value={formatCurrency(liabilities)} color="#EF4444" />
        <ResultCard label="Net worth" value={formatCurrency(netWorth)} color={netWorth >= 0 ? "#4F7FFA" : "#EF4444"} />
        <ResultCard label="Asset to debt ratio" value={liabilities > 0 ? `${(assets / liabilities).toFixed(2)}x` : "∞"} color="#A855F7" />
      </div>
    </div>
  );
}

// 8. Emergency Fund Calculator
function EmergencyFund() {
  const [monthlyExpenses, setMonthlyExpenses] = useState("3500");
  const [months, setMonths] = useState("6");
  const [currentSavings, setCurrentSavings] = useState("2000");
  const [monthlySavings, setMonthlySavings] = useState("300");

  const target = (parseFloat(monthlyExpenses) || 0) * (parseFloat(months) || 0);
  const current = parseFloat(currentSavings) || 0;
  const monthly = parseFloat(monthlySavings) || 0;
  const gap = Math.max(target - current, 0);
  const monthsToGoal = monthly > 0 ? Math.ceil(gap / monthly) : null;
  const progress = target > 0 ? Math.min((current / target) * 100, 100) : 0;

  const goalDate = monthsToGoal ? (() => {
    const d = new Date(); d.setMonth(d.getMonth() + monthsToGoal);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  })() : null;

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Monthly Expenses ($)" value={monthlyExpenses} onChange={setMonthlyExpenses} prefix="$" />
        <InputField label="Months of Coverage" value={months} onChange={setMonths} suffix="mo" />
        <InputField label="Current Savings ($)" value={currentSavings} onChange={setCurrentSavings} prefix="$" />
        <InputField label="Monthly Savings ($)" value={monthlySavings} onChange={setMonthlySavings} prefix="$" />
      </div>
      <div style={s.resultsGrid}>
        <ResultCard label="Emergency fund target" value={formatCurrency(target)} color="#4F7FFA" />
        <ResultCard label="Current savings" value={formatCurrency(current)} color="#10B981" />
        <ResultCard label="Amount still needed" value={formatCurrency(gap)} color={gap === 0 ? "#10B981" : "#EF4444"} />
        {goalDate && <ResultCard label="Goal reached by" value={goalDate} color="#A855F7" />}
      </div>
      <div style={s.progressSection}>
        <div style={s.progressLabel}>
          Progress: {progress.toFixed(1)}% funded
          {progress >= 100 && " 🎉 Goal reached!"}
        </div>
        <div style={s.progressBg}>
          <div style={{ ...s.progressFill, width: `${progress}%`, background: progress >= 100 ? "#10B981" : "#4F7FFA" }} />
        </div>
      </div>
    </div>
  );
}

// 9. Savings Goal Calculator
function SavingsGoal() {
  const [goal, setGoal] = useState("20000");
  const [current, setCurrent] = useState("3000");
  const [rate, setRate] = useState("4.5");
  const [years, setYears] = useState("3");

  const g = parseFloat(goal) || 0;
  const c = parseFloat(current) || 0;
  const r = parseFloat(rate) / 100 / 12;
  const n = (parseFloat(years) || 0) * 12;
  const gap = Math.max(g - c * Math.pow(1 + r, n), 0);

  let monthlyNeeded = 0;
  if (n > 0 && r > 0) {
    monthlyNeeded = gap * r / (Math.pow(1 + r, n) - 1);
  } else if (n > 0) {
    monthlyNeeded = gap / n;
  }

  const futureValueOfCurrent = c * Math.pow(1 + r, n);
  const totalContributions = monthlyNeeded * n;
  const progress = g > 0 ? Math.min((c / g) * 100, 100) : 0;

  const goalDate = (() => {
    const d = new Date(); d.setMonth(d.getMonth() + n);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  })();

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Savings Goal ($)" value={goal} onChange={setGoal} prefix="$" />
        <InputField label="Current Savings ($)" value={current} onChange={setCurrent} prefix="$" />
        <InputField label="Annual Interest Rate (%)" value={rate} onChange={setRate} suffix="%" />
        <InputField label="Time to Goal (years)" value={years} onChange={setYears} suffix="yrs" />
      </div>
      <div style={s.resultsGrid}>
        <ResultCard label="Monthly savings needed" value={formatCurrency(monthlyNeeded)} color="#4F7FFA" />
        <ResultCard label="Goal date" value={goalDate} color="#10B981" />
        <ResultCard label="Your savings will grow to" value={formatCurrency(futureValueOfCurrent)} color="#A855F7" small />
        <ResultCard label="Total contributions needed" value={formatCurrency(totalContributions)} color="#F97316" small />
      </div>
      <div style={s.progressSection}>
        <div style={s.progressLabel}>Current progress: {progress.toFixed(1)}% of goal</div>
        <div style={s.progressBg}>
          <div style={{ ...s.progressFill, width: `${progress}%`, background: "#10B981" }} />
        </div>
      </div>
    </div>
  );
}

// 10. Extra Payment Impact Calculator
function ExtraPaymentImpact() {
  const [balance, setBalance] = useState("15000");
  const [rate, setRate] = useState("8.5");
  const [payment, setPayment] = useState("300");
  const [extra, setExtra] = useState("100");

  const b = parseFloat(balance) || 0;
  const r = parseFloat(rate) / 100 / 12;
  const p = parseFloat(payment) || 0;
  const e = parseFloat(extra) || 0;

  function simulate(monthlyPayment) {
    if (monthlyPayment <= b * r || b <= 0) return null;
    const months = Math.ceil(Math.log(monthlyPayment / (monthlyPayment - b * r)) / Math.log(1 + r));
    return { months, totalInterest: months * monthlyPayment - b };
  }

  const base = simulate(p);
  const withExtra = simulate(p + e);

  const interestSaved = base && withExtra ? base.totalInterest - withExtra.totalInterest : 0;
  const monthsSaved = base && withExtra ? base.months - withExtra.months : 0;

  const payoffDate = withExtra ? (() => {
    const d = new Date(); d.setMonth(d.getMonth() + withExtra.months);
    return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  })() : null;

  return (
    <div>
      <div style={s.inputGrid}>
        <InputField label="Loan Balance ($)" value={balance} onChange={setBalance} prefix="$" />
        <InputField label="Annual Interest Rate (%)" value={rate} onChange={setRate} suffix="%" />
        <InputField label="Current Monthly Payment ($)" value={payment} onChange={setPayment} prefix="$" />
        <InputField label="Extra Monthly Payment ($)" value={extra} onChange={setExtra} prefix="$" />
      </div>
      {base && withExtra && (
        <>
          <div style={s.comparisonGrid}>
            <div style={s.comparisonCard}>
              <div style={s.comparisonLabel}>Without extra payment</div>
              <div style={s.comparisonValue}>{base.months} months</div>
              <div style={s.comparisonSub}>{formatCurrency(base.totalInterest)} interest</div>
            </div>
            <div style={{ ...s.comparisonCard, border: "2px solid #10B981" }}>
              <div style={{ ...s.comparisonLabel, color: "#10B981" }}>With extra {formatCurrency(e)}/mo</div>
              <div style={{ ...s.comparisonValue, color: "#10B981" }}>{withExtra.months} months</div>
              <div style={s.comparisonSub}>{formatCurrency(withExtra.totalInterest)} interest</div>
            </div>
          </div>
          <div style={s.resultsGrid}>
            <ResultCard label="Interest saved" value={formatCurrency(interestSaved)} color="#10B981" />
            <ResultCard label="Months saved" value={`${monthsSaved} months`} color="#4F7FFA" />
            <ResultCard label="New payoff date" value={payoffDate} color="#A855F7" />
            <ResultCard label="ROI on extra payments" value={`${e > 0 ? ((interestSaved / (e * withExtra.months)) * 100).toFixed(0) : 0}%`} color="#F97316" />
          </div>
          {interestSaved > 0 && (
            <div style={s.impactBanner}>
              💡 Paying just <strong>{formatCurrency(e)} more per month</strong> saves you <strong style={{ color: "#10B981" }}>{formatCurrency(interestSaved)}</strong> in interest and gets you debt-free <strong style={{ color: "#10B981" }}>{monthsSaved} months sooner!</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// 11. Loan Comparison Calculator
function LoanComparison() {
  const [loans, setLoans] = useState([
    { id: 1, label: "Loan A", amount: "25000", rate: "5.5", years: "60", extra: "0" },
    { id: 2, label: "Loan B", amount: "25000", rate: "7.0", years: "60", extra: "0" },
  ]);

  const [termUnit, setTermUnit] = useState("months");

  const updateLoan = (id, field, value) => {
    setLoans(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const toggleTermUnit = () => {
    const next = termUnit === "months" ? "years" : "months";
    setLoans(prev => prev.map(l => ({
      ...l,
      years: next === "years"
        ? String(Math.round(parseFloat(l.years) / 12))
        : String(Math.round(parseFloat(l.years) * 12))
    })));
    setTermUnit(next);
  };

  const addLoan = () => {
    if (loans.length >= 4) return;
    const id = Date.now();
    setLoans(prev => [...prev, { id, label: `Loan ${String.fromCharCode(64 + prev.length + 1)}`, amount: "25000", rate: "6.0", years: "60", extra: "0" }]);
  };

  const removeLoan = (id) => {
    if (loans.length <= 2) return;
    setLoans(prev => prev.filter(l => l.id !== id));
  };

  const calcLoan = (loan) => {
    const p = parseFloat(loan.amount) || 0;
    const r = parseFloat(loan.rate) / 100 / 12;
    const n = termUnit === "years" ? (parseFloat(loan.years) || 0) * 12 : (parseFloat(loan.years) || 0);
    const extra = parseFloat(loan.extra) || 0;
    if (p <= 0 || n <= 0) return null;

    // Base monthly payment
    let monthly = 0;
    if (r > 0) {
      monthly = (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    } else {
      monthly = p / n;
    }

    // Simulate with extra payment
    let balance = p;
    let totalInterest = 0;
    let months = 0;
    while (balance > 0.005 && months < 600) {
      months++;
      const interest = balance * r;
      totalInterest += interest;
      const principal = Math.min(monthly + extra - interest, balance);
      balance = Math.max(balance - principal, 0);
    }

    // eslint-disable-next-line no-unused-vars
    const totalPaid = monthly * Math.min(months, n) + extra * months - Math.max(0, balance);
    const payoffDate = new Date();
    payoffDate.setMonth(payoffDate.getMonth() + months);

    return {
      monthly: monthly + extra,
      baseMonthly: monthly,
      totalInterest,
      totalPaid: p + totalInterest,
      months,
      payoffDate: payoffDate.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
    };
  };

  const results = loans.map(l => ({ ...l, result: calcLoan(l) }));
  const best = results.reduce((b, l) => {
    if (!l.result) return b;
    if (!b || l.result.totalInterest < b.result.totalInterest) return l;
    return b;
  }, null);

  const LOAN_COLORS = ["#4F7FFA", "#10B981", "#F97316", "#A855F7"];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: "#888" }}>Compare up to 4 loans side by side</div>
        {loans.length < 4 && (
          <button style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 600, color: "#4338CA", cursor: "pointer" }} onClick={addLoan}>
            + Add Loan
          </button>
        )}
      </div>

      {/* Loan Inputs */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${loans.length}, 1fr)`, gap: 16, marginBottom: 24 }}>
        {loans.map((loan, idx) => {
          const color = LOAN_COLORS[idx % LOAN_COLORS.length];
          return (
            <div key={loan.id} style={{ background: "#F8F9FC", borderRadius: 12, padding: 16, border: `2px solid ${color}33` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color }}>{loan.label}</div>
                {loans.length > 2 && (
                  <button style={{ background: "none", border: "none", color: "#ccc", cursor: "pointer", fontSize: 16 }} onClick={() => removeLoan(loan.id)}>✕</button>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={s.fieldGroup}>
                  <label style={s.label}>Loan Amount ($)</label>
                  <div style={s.inputWrap}><span style={s.affix}>$</span><input style={s.input} type="number" min="0" value={loan.amount} onChange={e => updateLoan(loan.id, "amount", e.target.value)} /></div>
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>Interest Rate (%)</label>
                  <div style={s.inputWrap}><input style={{ ...s.input, borderRight: "none" }} type="number" min="0" value={loan.rate} onChange={e => updateLoan(loan.id, "rate", e.target.value)} /><span style={s.suffixAffix}>%</span></div>
                </div>
                <div style={s.fieldGroup}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <label style={s.label}>Term</label>
                    {loan.id === loans[0].id && (
                      <button onClick={toggleTermUnit} style={{ background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, color: "#4338CA", cursor: "pointer" }}>
                        {termUnit === "months" ? "Switch to Years" : "Switch to Months"}
                      </button>
                    )}
                  </div>
                  <div style={s.inputWrap}><input style={{ ...s.input, borderRight: "none" }} type="number" min="0" value={loan.years} onChange={e => updateLoan(loan.id, "years", e.target.value)} /><span style={s.suffixAffix}>{termUnit === "months" ? "mo" : "yrs"}</span></div>
                </div>
                <div style={s.fieldGroup}>
                  <label style={s.label}>Extra Payment ($)</label>
                  <div style={s.inputWrap}><span style={s.affix}>$</span><input style={s.input} type="number" min="0" value={loan.extra} onChange={e => updateLoan(loan.id, "extra", e.target.value)} /></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Results Comparison */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${loans.length}, 1fr)`, gap: 16, marginBottom: 20 }}>
        {results.map((loan, idx) => {
          const color = LOAN_COLORS[idx % LOAN_COLORS.length];
          const r = loan.result;
          const isBest = best?.id === loan.id;
          if (!r) return null;
          return (
            <div key={loan.id} style={{ background: "#fff", borderRadius: 12, padding: 16, border: `2px solid ${isBest ? color : "#E5E7EB"}`, position: "relative" }}>
              {isBest && (
                <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: color, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 20, whiteSpace: "nowrap" }}>
                  ✓ BEST DEAL
                </div>
              )}
              <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 12 }}>{loan.label}</div>
              {[
                { label: "Monthly Payment", value: formatCurrency(r.monthly), color: color },
                { label: "Total Interest", value: formatCurrency(r.totalInterest), color: "#EF4444" },
                { label: "Total Paid", value: formatCurrency(r.totalPaid), color: "#F97316" },
                { label: "Payoff Date", value: r.payoffDate, color: "#10B981" },
                { label: "Months", value: `${r.months} mo`, color: "#888" },
              ].map(row => (
                <div key={row.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "5px 0", borderBottom: "1px solid #F0F0F5" }}>
                  <span style={{ color: "#888" }}>{row.label}</span>
                  <span style={{ fontWeight: 700, color: row.color }}>{row.value}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Savings Summary */}
      {results.length >= 2 && results[0].result && results[1].result && (
        <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", fontSize: 13, color: "#166534", lineHeight: 1.8 }}>
          💡 {best?.label} saves you the most —{" "}
          <strong style={{ color: "#10B981" }}>
            {formatCurrency(Math.max(...results.map(l => l.result?.totalInterest || 0)) - (best?.result?.totalInterest || 0))}
          </strong>{" "}
          less in interest compared to the most expensive option.
        </div>
      )}
    </div>
  );
}

const CALCULATORS = [
  { id: "extra-payment", title: "Extra Payment Impact", icon: "🚀", color: "#14B8A6", component: ExtraPaymentImpact },
  { id: "debt-payoff", title: "Debt Payoff", icon: "💳", color: "#4F7FFA", component: DebtPayoff },
  { id: "loan-payment", title: "Loan Payment", icon: "🏦", color: "#F97316", component: LoanPayment },
  { id: "loan-comparison", title: "Loan Comparison", icon: "⚖️", color: "#6366F1", component: LoanComparison },
  { id: "credit-card", title: "Credit Card Payoff", icon: "💰", color: "#EF4444", component: CreditCardPayoff },
  { id: "compound-interest", title: "Compound Interest", icon: "📈", color: "#10B981", component: CompoundInterest },
  { id: "budget", title: "Budget Calculator", icon: "📊", color: "#A855F7", component: BudgetCalc },
  { id: "dti", title: "Debt-to-Income Ratio", icon: "⚖️", color: "#F59E0B", component: DebtToIncome },
  { id: "net-worth", title: "Net Worth", icon: "🏆", color: "#06B6D4", component: NetWorth },
  { id: "emergency-fund", title: "Emergency Fund", icon: "🛡️", color: "#EC4899", component: EmergencyFund },
  { id: "savings-goal", title: "Savings Goal", icon: "🎯", color: "#8B5CF6", component: SavingsGoal },
];

export default function Calculators({ onBack }) {
  const isMobile = useIsMobile();
  const [activeCalc, setActiveCalc] = useState("extra-payment");

  return (
    <div style={s.page}>

      <main style={{ ...s.main, ...(isMobile ? { padding: "16px 12px" } : {}) }}>
        <button style={s.backBtn} onClick={onBack}>← Back to Dashboard</button>
        <div style={{ ...s.pageTitle, ...(isMobile ? { fontSize: 20 } : {}) }}>🧮 Financial Calculators</div>
        <div style={s.pageSubtitle}>10 tools to help you understand and plan your financial future</div>

        {/* Calculator Grid Nav */}
        <div style={{ ...s.calcNav, ...(isMobile ? { gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))", gap: 8 } : {}) }}>
          {CALCULATORS.map((c) => (
            <button
              key={c.id}
              style={{ ...s.calcNavBtn, ...(activeCalc === c.id ? { background: c.color, color: "#fff", borderColor: c.color } : {}) }}
              onClick={() => setActiveCalc(c.id)}
            >
              <span style={{ fontSize: 16 }}>{c.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{c.title}</span>
            </button>
          ))}
        </div>

        {/* Active Calculator */}
        {CALCULATORS.map((c) => {
          if (c.id !== activeCalc) return null;
          const Component = c.component;
          return (
            <div key={c.id} style={{ ...s.calcCard, borderColor: c.color, boxShadow: `0 0 0 3px ${c.color}22` }}>
              <div style={{ ...s.calcCardHeader, background: c.color + "15" }}>
                <span style={s.calcIcon}>{c.icon}</span>
                <span style={{ ...s.calcTitle, color: c.color, fontSize: 18 }}>{c.title}</span>
              </div>
              <div style={s.calcBody}>
                <Component />
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
}

const s = {
  page: { minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" },
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", height: 64 },
  logo: { fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: 3 },
  logoSub: { fontSize: 11, color: "#8899BB", letterSpacing: 1, marginTop: 2, marginLeft: 10 },
  main: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px" },
  backBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer", color: "#555", marginBottom: 24, fontWeight: 500 },
  pageTitle: { fontSize: 26, fontWeight: 800, marginBottom: 6 },
  pageSubtitle: { fontSize: 14, color: "#888", marginBottom: 28 },
  calcNav: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10, marginBottom: 28 },
  calcNavBtn: { display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 8px", background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 10, cursor: "pointer", transition: "all 0.15s" },
  calcCard: { background: "#fff", borderRadius: 14, border: "2px solid transparent", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: 24 },
  calcCardHeader: { display: "flex", alignItems: "center", gap: 12, padding: "16px 24px" },
  calcIcon: { fontSize: 22 },
  calcTitle: { fontSize: 16, fontWeight: 700 },
  calcChevron: { marginLeft: "auto", color: "#888", fontSize: 12 },
  calcBody: { padding: "24px" },
  inputGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16, marginBottom: 24 },
  inputStack: { display: "flex", flexDirection: "column", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 13, fontWeight: 600, color: "#444" },
  inputWrap: { display: "flex", alignItems: "center", border: "1.5px solid #E5E7EB", borderRadius: 8, overflow: "hidden" },
  affix: { padding: "0 10px", background: "#F8F9FC", color: "#888", fontSize: 14, fontWeight: 600, borderRight: "1px solid #E5E7EB", height: "100%", display: "flex", alignItems: "center" },
  suffixAffix: { borderRight: "none", borderLeft: "1px solid #E5E7EB" },
  input: { border: "none", padding: "10px 12px", fontSize: 14, outline: "none", flex: 1, width: "100%" },
  inputWithPrefix: { borderLeft: "none" },
  inputWithSuffix: { borderRight: "none" },
  resultsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginBottom: 20 },
  resultCard: { background: "#F8F9FC", borderRadius: 10, padding: "14px 16px" },
  resultLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  resultValue: { fontSize: 24, fontWeight: 800 },
  warningBox: { background: "#FFF8F0", border: "1px solid #FFE0B2", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#555", marginBottom: 16 },
  toggleRow: { display: "flex", gap: 8, marginBottom: 20 },
  toggleBtn: { padding: "8px 16px", borderRadius: 8, border: "1.5px solid #E5E7EB", background: "#F3F4F6", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#555" },
  toggleBtnActive: { background: "#EEF2FF", borderColor: "#4F7FFA", color: "#4F7FFA" },
  budgetBars: { display: "flex", flexDirection: "column", gap: 10, marginTop: 8 },
  budgetBarRow: { display: "grid", gridTemplateColumns: "120px 1fr 80px 50px", alignItems: "center", gap: 10 },
  budgetBarLabel: { fontSize: 13, fontWeight: 500 },
  budgetBarBg: { background: "#F0F0F5", borderRadius: 4, height: 10, overflow: "hidden" },
  budgetBarFill: { height: "100%", borderRadius: 4, transition: "width 0.3s" },
  budgetBarAmt: { fontSize: 12, fontWeight: 600, textAlign: "right" },
  budgetBarPct: { fontSize: 11, color: "#888", textAlign: "right" },
  dtiBar: { position: "relative", background: "#F0F0F5", borderRadius: 6, height: 20, marginTop: 20, overflow: "visible" },
  dtiBarFill: { height: "100%", borderRadius: 6, transition: "width 0.3s" },
  dtiMark: { position: "absolute", top: 0, transform: "translateX(-50%)" },
  dtiMarkLine: { width: 1, height: 20, background: "#999", opacity: 0.5 },
  dtiMarkLabel: { fontSize: 10, color: "#888", textAlign: "center", marginTop: 4, whiteSpace: "nowrap" },
  twoColSection: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 },
  sectionSubtitle: { fontSize: 14, fontWeight: 700, marginBottom: 12 },
  progressSection: { marginTop: 8 },
  progressLabel: { fontSize: 13, fontWeight: 600, marginBottom: 8 },
  progressBg: { background: "#F0F0F5", borderRadius: 4, height: 12, overflow: "hidden" },
  progressFill: { height: "100%", borderRadius: 4, transition: "width 0.3s" },
  comparisonGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 },
  comparisonCard: { background: "#F8F9FC", borderRadius: 10, padding: "16px", border: "2px solid transparent", textAlign: "center" },
  comparisonLabel: { fontSize: 12, fontWeight: 600, color: "#888", marginBottom: 8 },
  comparisonValue: { fontSize: 24, fontWeight: 800, marginBottom: 4 },
  comparisonSub: { fontSize: 13, color: "#888" },
  impactBanner: { background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: "14px 16px", fontSize: 13, color: "#166534", lineHeight: 1.6 },
};
